/* eslint @typescript-eslint/no-explicit-any:0 */
import { NextConfig } from 'next';

interface RequestConfig {
  params?: object;
  headers?: Record<string, string>;
  body?: any;
  isFullUrl?: boolean;
  next?: NextConfig;
}

interface ClientConstructor {
  url: string;
  revalidate: number;
}

const DEFAULT_TIMEOUT = 8_000;

/**
 * HTTP-клиент для отправки запросов
 *
 * Ранее вместо него использовался axios
 * В NextJS 15 переопределили fetch, добавив к нему дополнительные параметры
 * Из-за чего для запросов нужно использовать именно fetch
 * https://nextjs.org/docs/app/api-reference/functions/fetch
 *
 * КЛИЕНТ ПОДДЕРЖИВАЕТ ТОЛЬКО JSON-формат
 *
 * Для обработки XML-запросов используйте обычный fetch()
 */

class CustomHttpClient {
  public DEFAULT_URL: string;
  public REVALIDATE: number;

  constructor({ url, revalidate }: ClientConstructor) {
    this.DEFAULT_URL = url;
    this.REVALIDATE = revalidate;
  }

  public async get<T = any>(url: string, config: RequestConfig = {}): Promise<T> {
    return this.request<T>('GET', url, config);
  }

  public async post<T = any>(url: string, config: RequestConfig = {}): Promise<T> {
    return this.request<T>('POST', url, config);
  }

  public async put<T = any>(url: string, config: RequestConfig = {}): Promise<T> {
    return this.request<T>('PUT', url, config);
  }

  public async patch<T = any>(url: string, config: RequestConfig = {}): Promise<T> {
    return this.request<T>('PATCH', url, config);
  }

  public async delete<T = any>(url: string, config: RequestConfig = {}): Promise<T> {
    return this.request<T>('DELETE', url, config);
  }

  private async request<T>(method: string, url: string, config: RequestConfig): Promise<T> {
    const { params, headers, body, isFullUrl, next: nextConfig } = config;
    const queryString = params ? this.objectToQueryString(params) : '';
    const baseUrl = isFullUrl ? url : `${this.DEFAULT_URL}${url}`;
    const requestUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;

    const response = await fetch(requestUrl, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: AbortSignal.timeout(DEFAULT_TIMEOUT),
      next: {
        revalidate: this.REVALIDATE,
        ...nextConfig,
      },
    });

    return response.json();
  }

  // Поддержка nested-объектов для опеределния query-string у запросов
  private objectToQueryString(obj: object, prefix?: string): string {
    const str = [];
    for (const p in obj) {
      if (obj.hasOwnProperty(p)) {
        const k = prefix ? prefix + '[' + p + ']' : p;
        //@ts-expect-error not needed
        const v = obj[p];
        if (v !== null && typeof v === 'object') {
          str.push(this.objectToQueryString(v, k));
        } else {
          str.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
        }
      }
    }
    return str.join('&');
  }
}

export default CustomHttpClient;
